// MSSQL JSON operator lattice.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2 (mssql row).
//
// MSSQL has no native JSON type — JSON columns reach the suggester through
// `check-isjson` / `loose-json-declared` / `loose-json-inferred`. The lattice
// is the same for all three kinds (JCS-10 — first-class treatment regardless
// of declaration mechanism). Includes the function-shape operators
// (`JSON_VALUE`, `JSON_QUERY`, `ISJSON`, `JSON_PATH_EXISTS`, `OPENJSON`) plus
// baseline `=` / `<>` / `LIKE` / `IS [NOT] NULL` per JCS-A3 CHECK ISJSON
// acceptance.

import type { Operator } from '../types';

const MSSQL_JSON: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= '${1:value}'" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> '${1:value}'" },
  {
    token: 'LIKE',
    label: 'matches pattern (raw text)',
    snippetTemplate: "LIKE '${1:%pattern%}'",
  },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  {
    token: 'JSON_VALUE',
    label: 'JSON path equals scalar value',
    snippetTemplate: "JSON_VALUE(${1:col}, '${2:$.path}') = '${3:value}'",
    docsUrl: 'https://learn.microsoft.com/sql/t-sql/functions/json-value-transact-sql',
    requiresPath: true,
  },
  {
    token: 'JSON_QUERY',
    label: 'JSON path returns object/array',
    snippetTemplate: "JSON_QUERY(${1:col}, '${2:$.path}') IS NOT NULL",
    docsUrl: 'https://learn.microsoft.com/sql/t-sql/functions/json-query-transact-sql',
    requiresPath: true,
  },
  {
    token: 'ISJSON',
    label: 'is well-formed JSON',
    snippetTemplate: 'ISJSON(${1:col}) > 0',
    docsUrl: 'https://learn.microsoft.com/sql/t-sql/functions/isjson-transact-sql',
  },
  {
    token: 'JSON_PATH_EXISTS',
    label: 'JSON path exists',
    snippetTemplate: "JSON_PATH_EXISTS(${1:col}, '${2:$.path}') = 1",
    docsUrl: 'https://learn.microsoft.com/sql/t-sql/functions/json-path-exists-transact-sql',
    requiresPath: true,
  },
  {
    token: 'EXISTS OPENJSON',
    label: 'JSON contains row matching predicate',
    snippetTemplate: 'EXISTS (SELECT 1 FROM OPENJSON(${1:col}) WHERE ${2:value} = ${3:expected})',
    docsUrl: 'https://learn.microsoft.com/sql/t-sql/functions/openjson-transact-sql',
  },
]);

export function mssqlJsonOperatorsFor(): readonly Operator[] {
  return MSSQL_JSON;
}
